// Write-back de georreferenciação ao próprio IFC: remove as entidades
// de georreferenciação existentes e escreve as novas com o placement
// efetivo do viewer.
//
// Tal como o patcher do IFCEdit, trabalha ao nível do texto STEP sobre
// os bytes originais em cache (rawIfc), sem round-trip pelo web-ifc,
// que reescreveria o ficheiro inteiro (e o WriteLine do 0.0.77 tem bugs
// conhecidos). Só as linhas de georreferenciação mudam; todo o resto do
// ficheiro fica byte a byte igual.
//
// IFC4+: IfcProjectedCRS + IfcMapConversion nativos.
// Convenções (espelham o ifc-georeferencer):
//   - MapUnit = a unidade dos eixos do EPSG (IfcSIUnit METRE novo, ou um
//     IfcConversionBasedUnit nos CRS em pés); Eastings/Northings/
//     OrthogonalHeight escritos nessa unidade, para o ficheiro não
//     contradizer o EPSG que nomeia.
//   - Scale = escala geométrica × fator de unidade (unidade do projeto →
//     unidade do mapa), por convenção IFC (Scale converte comprimentos do sistema
//     de engenharia para o CRS). Projeto em mm → Scale = 0.001.
//   - SourceCRS = primeiro IfcGeometricRepresentationContext do modelo.
//
// IFC2X3: não tem IfcMapConversion nativo. O "User Guide for
// Geo-referencing in IFC" v2.0 do buildingSMART faz o backport com dois
// property sets ligados ao IfcProject (não ao IfcSite: a
// georreferenciação aplica-se ao projeto inteiro):
//   - ePSet_ProjectedCRS (Name, MapUnit [, VerticalDatum]);
//   - ePSet_MapConversion (TargetCRS, Eastings, Northings,
//     OrthogonalHeight, XAxisAbscissa, XAxisOrdinate, Scale).
// Casing do guia: ePSet_ (S maiúsculo). Na leitura/remoção aceitamos
// qualquer casing, porque há ficheiros no terreno com ePset_.
// Unidades: E/N/h são coordenadas NO CRS de destino, logo vão na
// unidade de eixo do CRS (metro ou pé), tal como no caminho IFC4; o Scale
// leva a ponte de unidades (geométrica × unidade-projeto/unidade-CRS),
// por isso um projeto em mm escreve Scale = 0.001. Além disso marcamos
// a unidade duas vezes, porque o guia não define mecanismo nenhum: na
// propriedade MapUnit do ePSet_ProjectedCRS e no atributo Unit de cada
// IfcPropertySingleValue de comprimento (IfcSIUnit METRE, ou a unidade
// por conversão nos CRS em pés).
//
// Créditos: a abordagem (ePSets, ancoragem, convenções de unidade) vem
// do ifc-georeferencer da bedrock-engineer / buildingSMART NL,
// licenciado Apache-2.0 <https://github.com/bedrock-engineer/ifc-georeferencer>.
// A implementação aqui é própria (patch textual STEP, sem web-ifc), mas
// as convenções gravadas seguem as deles para os ficheiros serem
// interoperáveis entre as duas ferramentas.

import { metrosDoCrsConhecido, normalizeEpsg } from '@viewer/map/crsDefs'
import { lerUnidadeDoProjecto } from './georef/leituraDoMapa'
import type { GeoPlacement } from './geoTransform'
import {
  bytesToLatin1,
  findEntityById,
  findEntityByType,
  latin1ToBytes,
  splitTopLevelArgs,
  stepReal,
} from './stepText'

export interface GeorefWriteResult {
  bytes: Uint8Array
  /** Entidades de georreferenciação antigas removidas. */
  removed: number
  /** Esquema do ficheiro (ex.: IFC4). */
  schema: string
  /** Fator unidade do projeto → metros usado no Scale. */
  projectMetresPerUnit: number
}

const REMOVABLE_TYPES = [
  'IFCMAPCONVERSIONSCALED',
  'IFCMAPCONVERSION',
  'IFCRIGIDOPERATION',
  'IFCPROJECTEDCRS',
]

/** Escreve a georreferenciação nos bytes do IFC. Lança Error com
 *  mensagem em PT quando o ficheiro não o permite (IFC2X3, sem
 *  contexto geométrico, unidade não reconhecida, EPSG inválido). */
export function writeGeorefToIfc(
  bytes: Uint8Array,
  opts: {
    /** Código EPSG, aceita "EPSG:3763" ou "3763". */
    epsg: string
    /** Placement efetivo (E/N/h em metros, heading em graus, escala). */
    placement: GeoPlacement
    /** Datum vertical (ex.: "EPSG:5780"). undefined = preservar o do
     *  IfcProjectedCRS existente; null = escrever sem datum. */
    verticalDatum?: string | null
    /** Metros por unidade dos eixos do CRS (do índice EPSG: 1200/3937 nos
     *  pés americanos). Omitido: o do índice, se já estiver carregado,
     *  senão 1. E/N/h e o MapUnit vão nesta unidade, para o ficheiro não
     *  contradizer o EPSG que nomeia. */
    crsMetresPerUnit?: number | null
  },
): GeorefWriteResult {
  const epsgNum = Number(normalizeEpsg(String(opts.epsg)).replace(/^epsg:/i, '').trim())
  const crsUnit =
    opts.crsMetresPerUnit != null && Number.isFinite(opts.crsMetresPerUnit) && opts.crsMetresPerUnit > 0
      ? opts.crsMetresPerUnit
      : (metrosDoCrsConhecido(`EPSG:${epsgNum}`) ?? 1)
  if (!Number.isFinite(epsgNum) || epsgNum <= 0) {
    throw new Error(
      `CRS "${opts.epsg}" não é um código EPSG numérico. Define um código como EPSG:3763 no campo CRS.`,
    )
  }
  let text = bytesToLatin1(bytes)

  const schemaMatch = text.match(/FILE_SCHEMA\s*\(\s*\(\s*'([^']+)'/)
  const schema = schemaMatch ? schemaMatch[1] : '?'

  // Unidade de comprimento do projeto (para o Scale em IFC4 e para as
  // coordenadas dos ePsets em IFC2X3). Preferimos a que o
  // IfcUnitAssignment referencia; sem isso, o primeiro IFCSIUNIT
  // LENGTHUNIT do ficheiro.
  const projectMetresPerUnit = detectProjectMetresPerUnit(text)
  if (projectMetresPerUnit === null) {
    throw new Error(
      'Não foi possível determinar a unidade de comprimento do projeto ' +
        '(IfcSIUnit LENGTHUNIT). Sem ela as coordenadas gravadas ' +
        'ficariam erradas; ficheiro não alterado.',
    )
  }

  if (/^IFC2X/i.test(schema)) {
    // IFC2X3: convenção ePSet_MapConversion / ePSet_ProjectedCRS no
    // IfcProject (o esquema não tem IfcMapConversion nativo).
    return writeGeorefIfc2x3Text(text, {
      epsgNum,
      placement: opts.placement,
      verticalDatum: opts.verticalDatum,
      schema,
      projectMetresPerUnit,
      crsMetresPerUnit: crsUnit,
    })
  }

  // SourceCRS: primeiro IfcGeometricRepresentationContext (não sub).
  const ctxMatch = text.match(
    /#(\d+)\s*=\s*IFCGEOMETRICREPRESENTATIONCONTEXT\s*\(/,
  )
  if (!ctxMatch) {
    throw new Error(
      'O ficheiro não tem IfcGeometricRepresentationContext; não é ' +
        'possível ancorar o IfcMapConversion.',
    )
  }
  const ctxId = Number(ctxMatch[1])

  // Datum vertical: preservar o do IfcProjectedCRS existente quando o
  // caller não indicar um (4.º argumento posicional da entidade).
  let preservedVerticalDatum: string | null = null
  // A descrição e o datum geodésico (2.º e 3.º argumentos) mantêm-se
  // quando o CRS é o mesmo: reescrever o do EST do Cha139 apagava
  // "ETRS89 / Portugal TM06" e "ETRS89".
  let preservedDescription = '$'
  let preservedGeodeticDatum = '$'
  const crsEnt = findEntityByType(text, 'IFCPROJECTEDCRS')
  if (crsEnt) {
    const args = splitTopLevelArgs(crsEnt.args)
    const vd = args[3]?.trim() ?? ''
    if (vd.startsWith("'") && vd.endsWith("'") && vd.length >= 2) {
      preservedVerticalDatum = vd.slice(1, -1).replace(/''/g, "'")
    }
    const nome = (args[0] ?? '').trim().replace(/^'|'$/g, '')
    const mesmoCrs = Number(normalizeEpsg(nome).replace(/^epsg:/i, '').trim()) === epsgNum
    const texto = (v: string | undefined) => {
      const t = (v ?? '').trim()
      return t.startsWith("'") && t.endsWith("'") && t.length >= 2 ? t : '$'
    }
    if (mesmoCrs) {
      preservedDescription = texto(args[1])
      preservedGeodeticDatum = texto(args[2])
    }
  }
  const effectiveVerticalDatum =
    opts.verticalDatum === undefined
      ? preservedVerticalDatum
      : opts.verticalDatum

  // Remover georreferenciação existente (linhas completas, incluindo o
  // newline final para não deixar linhas em branco).
  let removed = 0
  for (const type of REMOVABLE_TYPES) {
    for (;;) {
      const ent = findEntityByType(text, type)
      if (!ent) break
      let end = ent.end
      if (text[end] === '\r') end++
      if (text[end] === '\n') end++
      text = text.slice(0, ent.start) + text.slice(end)
      removed++
    }
  }

  // IDs novos acima do máximo existente.
  let maxId = 0
  const idRe = /#(\d+)\s*=/g
  for (let m = idRe.exec(text); m; m = idRe.exec(text)) {
    const v = Number(m[1])
    if (v > maxId) maxId = v
  }
  const nl = text.includes('\r\n') ? '\r\n' : '\n'
  // MapUnit na unidade dos eixos do CRS (METRE, ou uma unidade por
  // conversão nos CRS em pés), e E/N/h e o Scale nessa unidade.
  const mapa = linhasDaUnidadeDoMapa(crsUnit, maxId, null, nl)
  const crsId = mapa.nextId + 1
  const mcId = mapa.nextId + 2

  const th = (opts.placement.rotationDeg * Math.PI) / 180
  const xaa = Math.cos(th)
  const xao = Math.sin(th)
  const onDiskScale = ((opts.placement.scale || 1) * projectMetresPerUnit) / crsUnit
  const datum =
    effectiveVerticalDatum && effectiveVerticalDatum.trim().length > 0
      ? `'${effectiveVerticalDatum.trim().replace(/'/g, "''")}'`
      : '$'

  const lines =
    mapa.lines +
    `#${crsId}=IFCPROJECTEDCRS('EPSG:${epsgNum}',${preservedDescription},${preservedGeodeticDatum},${datum},$,$,#${mapa.ref});${nl}` +
    `#${mcId}=IFCMAPCONVERSION(#${ctxId},#${crsId},${stepReal(opts.placement.eastings / crsUnit)},${stepReal(opts.placement.northings / crsUnit)},${stepReal(opts.placement.orthogonalHeight / crsUnit)},${stepReal(xaa)},${stepReal(xao)},${stepReal(onDiskScale)});${nl}`

  // Inserir antes do ENDSEC que fecha a secção DATA.
  const endsec = text.lastIndexOf('ENDSEC;')
  if (endsec < 0) {
    throw new Error('Ficheiro STEP sem ENDSEC; estrutura inesperada.')
  }
  text = text.slice(0, endsec) + lines + text.slice(endsec)

  return {
    bytes: latin1ToBytes(text),
    removed,
    schema,
    projectMetresPerUnit,
  }
}

// ---------------------------------------------------------------------------
// IFC2X3: ePSet_MapConversion / ePSet_ProjectedCRS no IfcProject.
// ---------------------------------------------------------------------------

const EPSET_NAMES = ['epset_mapconversion', 'epset_projectedcrs']

/** Nome da unidade dos eixos do CRS: o que se escreve no
 *  IfcConversionBasedUnit (IFC4) e no rótulo MapUnit do ePSet_ProjectedCRS
 *  (IFC2X3), na forma que leituraDoMapa.metrosDoRotulo lê de volta. */
function nomeDaUnidadeDoMapa(metros: number): string {
  if (Math.abs(metros - 1200 / 3937) < 1e-12) return 'US SURVEY FOOT'
  if (Math.abs(metros - 0.3048) < 1e-12) return 'FOOT'
  return `UNIT ${stepReal(metros)} M`
}

/** As linhas da unidade dos eixos do CRS (MapUnit). Em metros, um
 *  IfcSIUnit METRE (ou o que já existe, `metroExistente`); noutra
 *  unidade, um IfcConversionBasedUnit sobre o metro. Os ids novos
 *  começam em `ultimoId + 1`; `nextId` é o último usado. */
function linhasDaUnidadeDoMapa(
  metros: number,
  ultimoId: number,
  metroExistente: number | null,
  nl: string,
): { lines: string; ref: number; nextId: number } {
  let id = ultimoId
  let lines = ''
  let metro = metroExistente
  if (metro === null) {
    id++
    metro = id
    lines += `#${metro}=IFCSIUNIT(*,.LENGTHUNIT.,$,.METRE.);${nl}`
  }
  if (Math.abs(metros - 1) < 1e-12) return { lines, ref: metro, nextId: id }
  const dims = ++id
  const medida = ++id
  const unidade = ++id
  lines +=
    `#${dims}=IFCDIMENSIONALEXPONENTS(1,0,0,0,0,0,0);${nl}` +
    `#${medida}=IFCMEASUREWITHUNIT(IFCLENGTHMEASURE(${stepReal(metros)}),#${metro});${nl}` +
    `#${unidade}=IFCCONVERSIONBASEDUNIT(#${dims},.LENGTHUNIT.,'${nomeDaUnidadeDoMapa(metros)}',#${medida});${nl}`
  return { lines, ref: unidade, nextId: id }
}

/** GlobalId IFC novo: 128 bits aleatórios comprimidos em 22 carateres do
 *  alfabeto base64 do IFC. O primeiro caráter só codifica 2 bits (0-3). */
export function newIfcGuid(): string {
  const ALPHABET =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_$'
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  // 128 bits -> 22 dígitos base-64: o primeiro leva 2 bits, os restantes
  // 21 levam 6. Percorremos os bits de cima para baixo com um acumulador.
  let out = ''
  let acc = 0
  let accBits = 0
  let need = 2 // primeiro dígito: 2 bits
  for (const b of bytes) {
    acc = (acc << 8) | b
    accBits += 8
    while (accBits >= need) {
      accBits -= need
      const v = (acc >>> accBits) & ((1 << need) - 1)
      out += ALPHABET[v]
      acc &= (1 << accBits) - 1
      need = 6
    }
  }
  return out
}

/** Todos os expressIDs de entidades do tipo dado, no texto atual. */
function allIdsOfType(text: string, type: string): number[] {
  const re = new RegExp(`#(\\d+)\\s*=\\s*${type}\\s*\\(`, 'g')
  const out: number[] = []
  for (let m = re.exec(text); m; m = re.exec(text)) out.push(Number(m[1]))
  return out
}

/** Remove a linha inteira da entidade com este id (incluindo o newline). */
function removeEntityById(text: string, id: number): string {
  const ent = findEntityById(text, id)
  if (!ent) return text
  let end = ent.end
  if (text[end] === '\r') end++
  if (text[end] === '\n') end++
  return text.slice(0, ent.start) + text.slice(end)
}

/** Valor string de uma IfcPropertySingleValue pelo nome, dentro de um
 *  conjunto de ids de propriedades; null quando ausente. */
function readSingleValueString(
  text: string,
  propIds: number[],
  propName: string,
): string | null {
  for (const pid of propIds) {
    const ent = findEntityById(text, pid)
    if (!ent || ent.type !== 'IFCPROPERTYSINGLEVALUE') continue
    const args = splitTopLevelArgs(ent.args)
    const nm = args[0]?.trim() ?? ''
    if (nm !== `'${propName}'`) continue
    const nv = args[2]?.trim() ?? ''
    const m = nv.match(/^[A-Z]+\('((?:[^']|'')*)'\)$/)
    if (m) return m[1].replace(/''/g, "'")
  }
  return null
}

/** Escreve a georreferenciação num IFC2X3 via os ePSets do IfcProject.
 *  Textual como o caminho IFC4: remove os ePSets existentes (qualquer
 *  casing e ligados a qualquer host, o que migra os que versões antigas
 *  desta app puseram no IfcSite) e cunha entidades novas antes do
 *  ENDSEC. */
function writeGeorefIfc2x3Text(
  textIn: string,
  opts: {
    epsgNum: number
    placement: GeoPlacement
    /** undefined = preservar o VerticalDatum do ePSet existente. */
    verticalDatum: string | null | undefined
    schema: string
    projectMetresPerUnit: number
    /** Metros por unidade dos eixos do CRS. */
    crsMetresPerUnit: number
  },
): GeorefWriteResult {
  let text = textIn
  // Host = IfcProject: o guia do buildingSMART liga os ePSets ao
  // projeto porque a georreferenciação vale para o projeto inteiro (e
  // um ficheiro pode ter vários IfcSite). Versões antigas desta app e o
  // IfcGref punham-nos no IfcSite; a remissão em baixo apanha esses.
  const projectMatch = text.match(/#(\d+)\s*=\s*IFCPROJECT\s*\(/)
  if (!projectMatch) {
    throw new Error(
      'O ficheiro IFC2X3 não tem IfcProject; a convenção ' +
        'ePSet_MapConversion ancora-se no projeto, ficheiro não alterado.',
    )
  }
  const hostId = Number(projectMatch[1])
  // OwnerHistory do projeto (obrigatório em IfcRoot no IFC2X3):
  // reutilizado nas entidades novas, como faz o ifc-georeferencer.
  const hostEnt = findEntityById(text, hostId)
  const hostArgs = hostEnt ? splitTopLevelArgs(hostEnt.args) : []
  const oh = hostArgs[1]?.trim().match(/^#\d+$/) ? hostArgs[1].trim() : '$'

  // Inventário dos ePsets existentes (qualquer casing): pset + as suas
  // IfcPropertySingleValue + os rels que os ligam.
  const psetIdsToRemove: number[] = []
  const propIdsToRemove: number[] = []
  let preservedDatum: string | null = null
  for (const pid of allIdsOfType(text, 'IFCPROPERTYSET')) {
    const ent = findEntityById(text, pid)
    if (!ent) continue
    const args = splitTopLevelArgs(ent.args)
    const nameArg = args[2]?.trim() ?? ''
    const name = nameArg.replace(/^'|'$/g, '').toLowerCase()
    if (!EPSET_NAMES.includes(name)) continue
    psetIdsToRemove.push(pid)
    const list = args[4]?.trim() ?? ''
    const refs = list.match(/#\d+/g) ?? []
    const propIds = refs.map((r) => Number(r.slice(1)))
    propIdsToRemove.push(...propIds)
    if (name === 'epset_projectedcrs' && preservedDatum === null) {
      preservedDatum = readSingleValueString(text, propIds, 'VerticalDatum')
    }
  }
  const relIdsToRemove: number[] = []
  for (const rid of allIdsOfType(text, 'IFCRELDEFINESBYPROPERTIES')) {
    const ent = findEntityById(text, rid)
    if (!ent) continue
    const args = splitTopLevelArgs(ent.args)
    const target = args[5]?.trim() ?? ''
    const m = target.match(/^#(\d+)$/)
    if (m && psetIdsToRemove.includes(Number(m[1]))) relIdsToRemove.push(rid)
  }
  let removed = 0
  for (const id of [...relIdsToRemove, ...psetIdsToRemove, ...propIdsToRemove]) {
    const before = text.length
    text = removeEntityById(text, id)
    if (text.length !== before) removed++
  }

  const effectiveDatum =
    opts.verticalDatum === undefined ? preservedDatum : opts.verticalDatum

  // IDs novos acima do máximo existente.
  let maxId = 0
  const idRe = /#(\d+)\s*=/g
  for (let m = idRe.exec(text); m; m = idRe.exec(text)) {
    const v = Number(m[1])
    if (v > maxId) maxId = v
  }
  let nextId = maxId

  const th = (opts.placement.rotationDeg * Math.PI) / 180
  const xaa = Math.cos(th)
  const xao = Math.sin(th)
  // E/N/h são coordenadas NO CRS de destino: vão na unidade de eixo do
  // CRS (metro, ou pés nos CRS em pés), não na do projeto. O Scale leva a
  // ponte de unidades, igual ao caminho IFC4 (projeto em mm + CRS em
  // metro -> 0.001).
  const crsUnit = opts.crsMetresPerUnit
  const onDiskScale =
    (opts.placement.scale || 1) * (opts.projectMetresPerUnit / crsUnit)
  const crsName = `EPSG:${opts.epsgNum}`
  const esc = (s: string) => s.replace(/'/g, "''")
  const nl = text.includes('\r\n') ? '\r\n' : '\n'

  let lines = ''
  // Unidade explícita das propriedades de comprimento: reutiliza um
  // IfcSIUnit METRE que o ficheiro já tenha (evita acumular órfãos a
  // cada regravação) ou cunha um novo.
  // Num CRS em pés, a unidade das propriedades é a dos pés, sobre esse metro.
  const unidade = linhasDaUnidadeDoMapa(crsUnit, nextId, findMetreLengthUnitId(text), nl)
  lines += unidade.lines
  nextId = unidade.nextId
  const unitRef = unidade.ref
  const prop = (name: string, valueExpr: string, unit = '$'): string => {
    nextId++
    lines += `#${nextId}=IFCPROPERTYSINGLEVALUE('${name}',$,${valueExpr},${unit});${nl}`
    return `#${nextId}`
  }
  const lengthUnit = `#${unitRef}`
  // ePSet_ProjectedCRS. MapUnit não está no guia (que assume metros em
  // todo o lado), mas sem ela o ficheiro não se auto-descreve: escrevemo-la
  // a espelhar o IfcProjectedCRS.MapUnit do IFC4, como o ifc-georeferencer.
  const crsProps = [
    prop('Name', `IFCLABEL('${esc(crsName)}')`),
    prop('MapUnit', `IFCLABEL('${Math.abs(crsUnit - 1) < 1e-12 ? 'METRE' : nomeDaUnidadeDoMapa(crsUnit)}')`),
  ]
  if (effectiveDatum && effectiveDatum.trim().length > 0) {
    crsProps.push(
      prop('VerticalDatum', `IFCIDENTIFIER('${esc(effectiveDatum.trim())}')`),
    )
  }
  nextId++
  const crsPsetId = nextId
  lines += `#${crsPsetId}=IFCPROPERTYSET('${newIfcGuid()}',${oh},'ePSet_ProjectedCRS',$,(${crsProps.join(',')}));${nl}`
  nextId++
  lines += `#${nextId}=IFCRELDEFINESBYPROPERTIES('${newIfcGuid()}',${oh},$,$,(#${hostId}),#${crsPsetId});${nl}`
  // ePSet_MapConversion
  const mcProps = [
    prop('TargetCRS', `IFCLABEL('${esc(crsName)}')`),
    prop(
      'Eastings',
      `IFCLENGTHMEASURE(${stepReal(opts.placement.eastings / crsUnit)})`,
      lengthUnit,
    ),
    prop(
      'Northings',
      `IFCLENGTHMEASURE(${stepReal(opts.placement.northings / crsUnit)})`,
      lengthUnit,
    ),
    prop(
      'OrthogonalHeight',
      `IFCLENGTHMEASURE(${stepReal(opts.placement.orthogonalHeight / crsUnit)})`,
      lengthUnit,
    ),
    prop('XAxisAbscissa', `IFCREAL(${stepReal(xaa)})`),
    prop('XAxisOrdinate', `IFCREAL(${stepReal(xao)})`),
    prop('Scale', `IFCREAL(${stepReal(onDiskScale)})`),
  ]
  nextId++
  const mcPsetId = nextId
  lines += `#${mcPsetId}=IFCPROPERTYSET('${newIfcGuid()}',${oh},'ePSet_MapConversion',$,(${mcProps.join(',')}));${nl}`
  nextId++
  lines += `#${nextId}=IFCRELDEFINESBYPROPERTIES('${newIfcGuid()}',${oh},$,$,(#${hostId}),#${mcPsetId});${nl}`

  const endsec = text.lastIndexOf('ENDSEC;')
  if (endsec < 0) {
    throw new Error('Ficheiro STEP sem ENDSEC; estrutura inesperada.')
  }
  text = text.slice(0, endsec) + lines + text.slice(endsec)

  return {
    bytes: latin1ToBytes(text),
    removed,
    schema: opts.schema,
    projectMetresPerUnit: opts.projectMetresPerUnit,
  }
}

/** Fator unidade do projeto → metros lido TEXTUALMENTE do STEP.
 *  null quando não há IFCSIUNIT LENGTHUNIT reconhecível (ex.: unidades
 *  imperiais via IfcConversionBasedUnit). Partilhado com o write-back
 *  de placements (sitePlacementWrite).
 *
 *  Usar este detetor no caminho de ESCRITA, que trabalha só sobre os
 *  bytes e tem de ser puro/testável sem modelo carregado. Quando o
 *  modelo já está no parser paralelo, o equivalente mais robusto é
 *  `rawIfc.getLengthUnit(modelId)` (cobre IfcConversionBasedUnit). Os
 *  dois vivem em camadas diferentes de propósito: um sobre texto, o
 *  outro sobre o modelo parseado. */
export function detectProjectMetresPerUnit(text: string): number | null {
  // A unidade de comprimento do IfcUnitAssignment do IfcProject, SI ou
  // por conversão (pés, polegadas). Antes, quando a unidade referida não
  // se reconhecia, ficava "a primeira" do ficheiro, que podia ser o
  // MapUnit; e duas unidades de comprimento davam a primeira. Agora o
  // que não se sabe sem escolher devolve null, e quem escreve recusa.
  return lerUnidadeDoProjecto(text).unidade?.metros ?? null
}

/** Mapa expressID → metros por unidade de todos os IFCSIUNIT LENGTHUNIT
 *  METRE do texto (o prefixo dá o fator). */
function scanLengthUnits(text: string): Map<number, number> {
  const PREFIX_FACTOR: Record<string, number> = {
    '': 1,
    MILLI: 0.001,
    CENTI: 0.01,
    DECI: 0.1,
    KILO: 1000,
  }
  const unitRe =
    /#(\d+)\s*=\s*IFCSIUNIT\s*\(\s*\*\s*,\s*\.LENGTHUNIT\.\s*,\s*(\$|\.([A-Z]+)\.)\s*,\s*\.METRE\.\s*\)/g
  const found = new Map<number, number>()
  for (let m = unitRe.exec(text); m; m = unitRe.exec(text)) {
    const prefix = m[3] ?? ''
    const factor = PREFIX_FACTOR[prefix]
    if (factor !== undefined) found.set(Number(m[1]), factor)
  }
  return found
}

/** expressID de um IFCSIUNIT LENGTHUNIT METRE sem prefixo já presente no
 *  ficheiro (fator 1), ou null. Usado para o atributo Unit das
 *  propriedades de comprimento dos ePSets sem duplicar entidades. */
function findMetreLengthUnitId(text: string): number | null {
  for (const [id, factor] of scanLengthUnits(text)) {
    if (factor === 1) return id
  }
  return null
}
